import React from 'react'
import { Button, Divider, Hint } from '@tecsinapse/cortex-react'
import { useGruposUsuariosAtivos } from '@/core/hooks/use-grupos-de-usuarios'
import { OptionSelect } from '@/core/utils/types'
import { Loading } from '@/core/components/loading'
import { FormTextField } from '../../hook-form-connectors/form-text-field'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import FormSingleSelect from '../../hook-form-connectors/form-single-select'
import { FormSelectGrupoUsuarios } from '../../hook-form-connectors/form-select-grupo-usuarios'
import { useFormContext, useWatch } from 'react-hook-form'
import { NovaPastaValues } from '../hooks/use-nova-pasta-dialog'
import { CONST_ROOT_FOLDER, VisibilidadePasta } from '../utils/constants'
import { ItemBiblioteca } from '@/core/types/graphql'
import { useParams } from 'react-router'

interface FormNewFolderFragmentProps {
  submitLabel: string
  pastaOptions?: OptionSelect[]
  showName?: boolean
  showLocation?: boolean
  showPermissions?: boolean
  closeForm?: () => void
  pastaAtual?: ItemBiblioteca
}

export const FormNewFolderFragment = ({
  pastaOptions,
  showName = false,
  showLocation = false,
  showPermissions = false,
  closeForm,
  submitLabel,
  pastaAtual
}: FormNewFolderFragmentProps) => {
  const { idPasta } = useParams()
  const isRoot = !idPasta

  const {
    setValue,
    formState: { isSubmitting, isDirty }
  } = useFormContext()
  const values = useWatch<NovaPastaValues>()

  const { gruposOptions, loading } = useGruposUsuariosAtivos()

  if (loading) return <Loading />

  return (
    <div className={'flex flex-col space-y-deca'}>
      {showName && (
        <div>
          <FormTextField name='nome' label={t`Nome`} />
        </div>
      )}
      {showLocation ? (
        <>
          <FormSingleSelect
            name='idPai'
            label={t`Localização`}
            options={pastaOptions}
            customOnChange={() => {
              setValue('idsGrupoAcesso', [], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              })
              setValue('idsGrupoAcessoEscrita', [], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              })
            }}
          />
          {!isRoot ? (
            <Hint
              variants={{ intent: 'error', className: 'text-micro' }}
            >{`A pasta selecionada possui visibilidade ${pastaAtual?.isPublico ? 'Pública' : 'Privada'}, e a subpasta ${values.nome} terá a mesma visibilidade.`}</Hint>
          ) : null}
        </>
      ) : null}
      {showPermissions || values.idPai?.value === CONST_ROOT_FOLDER ? (
        <>
          {showLocation && <Divider />}
          <div className={'flex flex-col gap-deca'}>
            <FormSingleSelect
              name='isPublico'
              options={[VisibilidadePasta.PRIVADA, VisibilidadePasta.PUBLICA]}
              label={t`Visibilidade da Pasta`}
            />
            <FormSelectGrupoUsuarios
              name='idsGrupoAcesso'
              label={t`Grupos com Acesso a Leitura`}
              disabled={
                gruposOptions.length === 0 || values.isPublico?.value !== true
              }
              disableActions
            />

            <FormSelectGrupoUsuarios
              name='idsGrupoAcessoEscrita'
              label={t`Grupos com Acesso a Leitura e Edição`}
              disabled={
                gruposOptions.length === 0 || values.isPublico?.value !== true
              }
              disableActions
            />
          </div>
        </>
      ) : null}
      <div className={'flex flex-row justify-between items-center mt-deca'}>
        <Button
          disabled={isSubmitting}
          type='button'
          onClick={closeForm}
          variants={{ variant: 'outline', className: 'w-full' }}
        >
          <p>
            <Trans>Cancelar</Trans>
          </p>
        </Button>
        <Button
          disabled={isSubmitting || gruposOptions.length === 0 || !isDirty}
          type='submit'
          variants={{ className: 'w-full' }}
        >
          <p>
            <Trans>{submitLabel}</Trans>
          </p>
        </Button>
      </div>
    </div>
  )
}
